import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, useResource, errorText } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import RecordDialog from "@/components/RecordDialog";
import ConfirmDelete from "@/components/ConfirmDelete";

export default function AdjustmentsTab({ directors }) {
  const api = useApi();
  const { data, reload } = useResource("/director_adjustments");
  const [editing, setEditing] = useState(null);
  const fields = [
    { key: "director", label: "Director", type: "select", options: directors.map((d) => ({ value: d.id, label: d.name })) },
    { key: "amount", label: "Amount", type: "decimal" },
    { key: "note", label: "Note", type: "text", optional: true },
  ];
  const name = (id) => directors.find((d) => d.id === id)?.name || "—";

  const save = async (f) => {
    if (!f.director) return "Pick a director";
    const body = { director: f.director, amount: f.amount, note: f.note || "" };
    try {
      if (editing.id) await api("put", `/director_adjustments/${editing.id}`, body);
      else await api("post", "/director_adjustments", body);
      toast.success("Adjustment saved");
      setEditing(null);
      reload();
    } catch (e) { return errorText(e); }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center">
        <p className="text-sm text-muted-foreground">Manual adjustments sit on their own line in the settlement.</p>
        <Button className="ml-auto" onClick={() => setEditing({ director: "", amount: "", note: "" })} data-testid="add-adjustment-button">
          <Plus className="h-4 w-4 mr-1" /> Add adjustment
        </Button>
      </div>
      <div className="border border-border bg-card">
        <table className="w-full text-sm" data-testid="adjustments-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-5 py-3 font-medium">Director</th><th className="px-5 py-3 font-medium text-right">Amount</th>
              <th className="px-5 py-3 font-medium">Note</th><th />
            </tr>
          </thead>
          <tbody>
            {(data || []).map((a) => (
              <tr key={a.id} className="border-b border-border last:border-0" data-testid={`adjustment-row-${a.id}`}>
                <td className="px-5 py-3">{name(a.director)}</td>
                <td className="px-5 py-3 text-right font-mono">{formatMoney(a.amount)}</td>
                <td className="px-5 py-3 text-muted-foreground">{a.note}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(a)} data-testid={`adjustment-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete name={`${name(a.director)} adjustment`} testId={`adjustment-delete-${a.id}`}
                    onConfirm={async () => { try { await api("delete", `/director_adjustments/${a.id}`); toast.success("Adjustment deleted"); reload(); } catch (e) { toast.error(errorText(e)); } }} />
                </td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-muted-foreground">No adjustments.</td></tr>}
          </tbody>
        </table>
      </div>
      <RecordDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} prefix="adjustment-form"
        title={editing?.id ? "Edit adjustment" : "Add adjustment"} fields={fields} initial={editing || {}} onSubmit={save} />
    </div>
  );
}
